import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Plus, Trash2, Building2, Box, GripVertical,
  ChevronDown, ChevronRight, DollarSign, Filter, AlertTriangle,
  Tag, Settings, ListChecks, X,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { useScheduleData } from "@/hooks/useScheduleData";
import { equipmentApi } from "@/lib/api";
import { roomColor } from "@/components/schedule/FilterBar";
import ProjectSwitcher from "@/components/schedule/ProjectSwitcher";
import QtyPromptDialog from "@/components/schedule/QtyPromptDialog";
import EquipmentStatusTab, { CategoriesManagerDialog, StatusesManagerDialog } from "@/components/equipment/EquipmentStatusTab";
import CategorySelect from "@/components/equipment/CategorySelect";
import EquipmentEditDialog from "@/components/equipment/EquipmentEditDialog";

/**
 * Equipment view — split screen.
 *
 *   ┌────────── ROOMS ──────────┬──────── INVENTORY ────────┐
 *   │ Per-room sections showing │ All equipment with        │
 *   │ allocated qty per item.   │ available / shortfall.    │
 *   │ Drop inventory tile here  │ + new-equipment form,     │
 *   │   → qty prompt, sets the  │   category filter chips.  │
 *   │   allocation in that room.│                           │
 *   └───────────────────────────┴───────────────────────────┘
 *
 * Multi-room: each equipment row is project-wide inventory; per-room qty lives
 * in `equipment_allocations`. Available = quantity_total − Σ allocations (can
 * be negative → shortfall chip).
 *
 * Drag MIME types:
 *   application/x-ps-equipment-inventory  = drag from inventory side
 *   application/x-ps-equipment-allocation = drag from a room (one allocation)
 *
 * Payload: { equipment_id, allocation_id?, current_qty }
 */

const MIME_INVENTORY = "application/x-ps-equipment-inventory";
const MIME_ALLOCATION = "application/x-ps-equipment-allocation";

const FILTER_OPTIONS = [
  { value: "all", label: "ALL" },
  { value: "hired", label: "HIRED" },
  { value: "purchased", label: "PURCHASED" },
  { value: "shortfall", label: "SHORTFALL" },
];

const KIND_STYLES = {
  hired: { dot: "bg-amber", text: "text-amber", label: "HIRED" },
  purchased: { dot: "bg-emerald-400", text: "text-emerald-400", label: "PURCHASED" },
};

export default function EquipmentView({ onSwitchToSchedule }) {
  const { user } = useAuth();
  const {
    projects, project, data, loading,
    selectProject, refresh,
  } = useScheduleData();

  const equipment = useMemo(() => (data?.equipment || []).filter((e) => !e.deleted_at), [data?.equipment]);
  const allocations = useMemo(() => (data?.equipment_allocations || []).filter((a) => !a.deleted_at), [data?.equipment_allocations]);
  const categoriesList = useMemo(() => data?.equipment_categories || [], [data?.equipment_categories]);
  const rooms = useMemo(() => data?.rooms || [], [data?.rooms]);

  const [tab, setTab] = useState("allocations"); // "allocations" | "status"
  const [filter, setFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState(null); // null = ALL categories
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // equipment object or null
  const [catsOpen, setCatsOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);

  // Qty prompt state for drag-to-allocate. Opens when an inventory tile lands
  // on a room.
  const [qtyPrompt, setQtyPrompt] = useState(null);
  // qtyPrompt = { equipment, room, currentQty, allocationId? }

  // ─── derived: per-equipment usage + per-room allocation list ──
  const { usageByEquipment, allocationsByRoom, allocationByPair } = useMemo(() => {
    const usage = new Map();      // equipment_id → total allocated qty
    const byRoom = new Map();     // room_id → [{ equipment, allocation, qty }]
    const byPair = new Map();     // `${eqId}|${roomId}` → allocation
    for (const r of rooms) byRoom.set(r.id, []);
    for (const e of equipment) {
      usage.set(e.id, 0);
    }
    for (const a of allocations) {
      usage.set(a.equipment_id, (usage.get(a.equipment_id) || 0) + (a.qty || 0));
      byPair.set(`${a.equipment_id}|${a.room_id}`, a);
      const e = equipment.find((x) => x.id === a.equipment_id);
      if (!e) continue;
      const arr = byRoom.get(a.room_id) || [];
      arr.push({ equipment: e, allocation: a, qty: a.qty });
      byRoom.set(a.room_id, arr);
    }
    // Sort allocations within each room alphabetically by label.
    for (const arr of byRoom.values()) {
      arr.sort((x, y) => (x.equipment.label || x.equipment.type).localeCompare(y.equipment.label || y.equipment.type));
    }
    return {
      usageByEquipment: usage,
      allocationsByRoom: byRoom,
      allocationByPair: byPair,
    };
  }, [equipment, allocations, rooms]);

  // Categories list. Prefer the project-managed list (categoriesList); fall
  // back to inferred-from-equipment for projects that haven't created any.
  // Returns plain string names for the filter chips.
  const categories = useMemo(() => {
    if (categoriesList && categoriesList.length) {
      return categoriesList.map((c) => c.name).sort((a, b) => a.localeCompare(b));
    }
    const cats = new Set();
    for (const e of equipment) if (e.category) cats.add(e.category);
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [categoriesList, equipment]);

  // ─── inventory tiles after filter ────────────────────────────
  const inventory = useMemo(() => {
    const q = search.trim().toLowerCase();
    return equipment
      .filter((e) => {
        if (categoryFilter != null && (e.category || "") !== categoryFilter) return false;
        if (filter === "hired" || filter === "purchased") return e.acquisition_kind === filter;
        if (filter === "shortfall") {
          const avail = (e.quantity_total || 0) - (usageByEquipment.get(e.id) || 0);
          return avail < 0;
        }
        return true;
      })
      .filter((e) => {
        // Free-text search across the human-readable fields.
        if (!q) return true;
        const haystack = [
          e.label, e.type, e.category, e.vendor, e.notes, e.acquisition_kind, e.status,
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (a.label || a.type).localeCompare(b.label || b.type));
  }, [equipment, filter, categoryFilter, search, usageByEquipment]);

  const counts = useMemo(() => {
    let nHired = 0, nPurchased = 0, nShortfall = 0;
    let totalAllocated = 0;
    for (const e of equipment) {
      if (e.acquisition_kind === "hired") nHired += 1;
      else if (e.acquisition_kind === "purchased") nPurchased += 1;
      const used = usageByEquipment.get(e.id) || 0;
      totalAllocated += used;
      if ((e.quantity_total || 0) - used < 0) nShortfall += 1;
    }
    return { total: equipment.length, hired: nHired, purchased: nPurchased, shortfall: nShortfall, totalAllocated };
  }, [equipment, usageByEquipment]);

  // ─── mutations ────────────────────────────────────────────────
  const patchEquipment = async (id, updates) => {
    try { await equipmentApi.update(id, updates); }
    catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      throw e;
    }
  };

  const setAllocation = async (equipmentId, roomId, qty) => {
    try {
      await equipmentApi.upsertAllocation(equipmentId, roomId, qty);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Allocation failed");
    }
  };

  const removeEquipment = async (id) => {
    try {
      await equipmentApi.remove(id);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ─── DnD: drop on room opens qty prompt ──────────────────────
  const openQtyPromptForDrop = (room, payload) => {
    const eq = equipment.find((e) => e.id === payload.equipment_id);
    if (!eq) return;
    const existing = allocationByPair.get(`${eq.id}|${room.id}`);
    setQtyPrompt({
      equipment: eq,
      room,
      currentQty: existing ? existing.qty : 1,
      allocationId: existing?.id || null,
      // For inventory drops we DEFAULT to "add one" unless an allocation
      // already exists — then default to the current qty (so Enter is no-op).
      defaultValue: existing ? existing.qty : 1,
      isMove: payload.kind === MIME_ALLOCATION,
      sourceAllocationId: payload.allocation_id,
    });
  };

  const confirmQtyPrompt = async (qty) => {
    if (!qtyPrompt) return;
    const { equipment: eq, room, isMove, sourceAllocationId } = qtyPrompt;
    setQtyPrompt(null);
    if (qty == null) return; // cancelled
    // If MOVING (drag from another room's allocation to this room): clear the
    // source allocation if the user kept the full qty here, else split.
    if (isMove && sourceAllocationId) {
      const src = allocations.find((a) => a.id === sourceAllocationId);
      if (src && src.room_id !== room.id) {
        // Set destination first (so we don't briefly drop to zero across both).
        await equipmentApi.upsertAllocation(eq.id, room.id, qty);
        if (qty >= src.qty) {
          await equipmentApi.removeAllocation(src.id);
        } else {
          await equipmentApi.updateAllocation(src.id, { qty: src.qty - qty });
        }
        await refresh();
        toast.success(`Moved ${qty} × ${eq.label || eq.type} → ${room.abbreviation || room.name}`);
        return;
      }
    }
    await setAllocation(eq.id, room.id, qty);
    if (qty > 0) {
      toast.success(`${qty} × ${eq.label || eq.type} → ${room.abbreviation || room.name}`);
    } else {
      toast.success(`Removed ${eq.label || eq.type} from ${room.abbreviation || room.name}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-bg-base flex items-center justify-center">
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute">LOADING EQUIPMENT…</div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-bg-base flex flex-col" data-testid="equipment-view">
      <Header
        onBack={onSwitchToSchedule}
        projects={projects}
        currentProjectId={project?.id}
        onSelectProject={selectProject}
        counts={counts}
        filter={filter}
        onFilterChange={setFilter}
        userName={user?.name}
        tab={tab}
        onTabChange={setTab}
        onOpenCategories={() => setCatsOpen(true)}
        onOpenStatuses={() => setStatusesOpen(true)}
      />

      {tab === "allocations" && (
        <>
          <CategoryBar
            categories={categories}
            active={categoryFilter}
            onChange={setCategoryFilter}
          />

          <div className="flex-1 flex min-h-0">
            <RoomsSide
              rooms={rooms}
              allocationsByRoom={allocationsByRoom}
              equipment={equipment}
              onDropOnRoom={openQtyPromptForDrop}
              onEditAllocation={(alloc) => {
                const eq = equipment.find((e) => e.id === alloc.equipment_id);
                const room = rooms.find((r) => r.id === alloc.room_id);
                if (!eq || !room) return;
                setQtyPrompt({
                  equipment: eq,
                  room,
                  currentQty: alloc.qty,
                  allocationId: alloc.id,
                  defaultValue: alloc.qty,
                  isMove: false,
                });
              }}
              onRemoveAllocation={async (alloc) => {
                await equipmentApi.removeAllocation(alloc.id);
                await refresh();
              }}
            />

            <InventorySide
              project={project}
              rooms={rooms}
              inventory={inventory}
              usageByEquipment={usageByEquipment}
              onPatchEquipment={patchEquipment}
              onRemoveEquipment={removeEquipment}
              onRefresh={refresh}
              filter={filter}
              categories={categories}
              search={search}
              onSearchChange={setSearch}
              onOpenEquipment={setEditing}
            />
          </div>
        </>
      )}

      {tab === "status" && (
        <EquipmentStatusTab
          project={project}
          rooms={rooms}
          tasks={data?.tasks || []}
          blocks={data?.schedule_blocks || []}
          equipment={equipment}
          allocations={allocations}
          categories={categoriesList}
          onRefresh={refresh}
        />
      )}

      <QtyPromptDialog
        open={!!qtyPrompt}
        title={qtyPrompt ? `${qtyPrompt.equipment.label || qtyPrompt.equipment.type} → ${qtyPrompt.room.abbreviation || qtyPrompt.room.name}` : ""}
        helper={qtyPrompt ? (
          (() => {
            const total = qtyPrompt.equipment.quantity_total || 0;
            const used = usageByEquipment.get(qtyPrompt.equipment.id) || 0;
            const here = qtyPrompt.currentQty;
            const elsewhere = used - here;
            const remaining = total - elsewhere;
            return `Pool ${total} · in other rooms ${elsewhere} · max here without shortfall ${remaining}`;
          })()
        ) : ""}
        defaultValue={qtyPrompt?.defaultValue || 1}
        onConfirm={confirmQtyPrompt}
        onCancel={() => setQtyPrompt(null)}
      />

      <CategoriesManagerDialog
        open={catsOpen}
        onClose={() => setCatsOpen(false)}
        projectId={project?.id}
        categories={categoriesList}
        onMutated={refresh}
      />

      <StatusesManagerDialog
        open={statusesOpen}
        onClose={() => setStatusesOpen(false)}
        project={project}
        onMutated={refresh}
      />

      <EquipmentEditDialog
        open={!!editing}
        equipment={editing}
        used={editing ? (usageByEquipment.get(editing.id) || 0) : 0}
        categories={categoriesList.length ? categoriesList : categories}
        projectId={project?.id}
        statuses={project?.equipment_statuses}
        onClose={() => setEditing(null)}
        onMutated={refresh}
        onDelete={async () => {
          if (!editing) return;
          await removeEquipment(editing.id);
          setEditing(null);
        }}
      />
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────

function Header({ onBack, projects, currentProjectId, onSelectProject, counts, filter, onFilterChange, userName, tab, onTabChange, onOpenCategories, onOpenStatuses }) {
  return (
    <header className="h-12 px-3 border-b border-line-strong flex items-center gap-3 bg-bg-base">
      <button onClick={onBack} className="btn-ghost flex items-center gap-1 px-2" data-testid="back-to-schedule">
        <ArrowLeft className="w-3 h-3" /><span className="hidden md:inline">SCHEDULE</span>
      </button>
      <div className="h-5 w-px bg-line" />
      <div className="mono text-2xs uppercase tracking-[0.2em] text-amber">EQUIPMENT</div>

      {/* TAB SWITCHER */}
      <div className="flex border border-line-strong" data-testid="eq-tab-bar">
        {[
          { v: "allocations", label: "ALLOCATIONS" },
          { v: "status", label: "STATUS" },
        ].map((t) => {
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              onClick={() => onTabChange(t.v)}
              data-testid={`eq-tab-${t.v}`}
              className={
                "mono text-2xs uppercase tracking-widest px-3 py-1 transition-colors " +
                (active ? "bg-amber text-bg-base" : "text-ink-mute hover:text-ink")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="h-5 w-px bg-line" />
      <ProjectSwitcher projects={projects || []} currentProjectId={currentProjectId} onSelect={onSelectProject} />

      <div className="flex-1" />

      {tab === "allocations" && (
        <div className="flex border border-line-strong" data-testid="eq-filter-bar">
          {FILTER_OPTIONS.map((opt) => {
            const active = filter === opt.value;
            const isShort = opt.value === "shortfall";
            return (
              <button
                key={opt.value}
                onClick={() => onFilterChange(opt.value)}
                data-testid={`eq-filter-${opt.value}`}
                className={
                  "mono text-2xs uppercase tracking-widest px-3 py-1 transition-colors flex items-center gap-1 " +
                  (active
                    ? (isShort ? "bg-red text-bg-base" : "bg-amber text-bg-base")
                    : (isShort && counts.shortfall > 0 ? "text-red hover:text-red-light" : "text-ink-mute hover:text-ink"))
                }
              >
                {isShort && <AlertTriangle className="w-3 h-3" />}
                {opt.label}
                {isShort && counts.shortfall > 0 && <span className="ml-1 tabular-nums">{counts.shortfall}</span>}
              </button>
            );
          })}
        </div>
      )}

      <button onClick={onOpenCategories} className="btn-ghost flex items-center gap-1 px-2" title="Manage categories" data-testid="eq-open-categories">
        <Tag className="w-3 h-3" /><span className="hidden md:inline">CATEGORIES</span>
      </button>
      <button onClick={onOpenStatuses} className="btn-ghost flex items-center gap-1 px-2" title="Customize status workflow" data-testid="eq-open-statuses">
        <ListChecks className="w-3 h-3" /><span className="hidden md:inline">STATUSES</span>
      </button>

      <div className="hidden md:flex mono text-2xs uppercase tracking-widest text-ink-mute gap-3 tabular-nums">
        <span><span className="text-cyan">{counts.totalAllocated}</span> ALLOC</span>
        <span className="opacity-40">·</span>
        <span><span className="text-amber">{counts.hired}</span> H · <span className="text-emerald-400">{counts.purchased}</span> P</span>
      </div>

      {userName && (
        <div className="mono text-2xs tracking-widest text-ink-mute hidden lg:block ml-3">{userName}</div>
      )}
    </header>
  );
}

// ── Category filter strip ───────────────────────────────────────

function CategoryBar({ categories, active, onChange }) {
  if (!categories || categories.length === 0) return null;
  return (
    <div className="px-3 py-1.5 border-b border-line bg-bg-elevated/40 flex items-center gap-2 overflow-x-auto" data-testid="eq-category-bar">
      <Filter className="w-3 h-3 text-ink-mute shrink-0" />
      <button
        onClick={() => onChange(null)}
        className={
          "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors shrink-0 " +
          (active == null ? "border-amber text-amber bg-amber-glow/15" : "border-line text-ink-mute hover:text-ink")
        }
        data-testid="eq-cat-all"
      >
        ALL
      </button>
      {categories.map((cat) => {
        const isActive = active === cat;
        return (
          <button
            key={cat}
            onClick={() => onChange(isActive ? null : cat)}
            className={
              "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors shrink-0 " +
              (isActive ? "border-amber text-amber bg-amber-glow/15" : "border-line text-ink-mute hover:text-ink")
            }
            data-testid={`eq-cat-${cat}`}
          >
            {cat}
          </button>
        );
      })}
    </div>
  );
}

// ── ROOMS side ──────────────────────────────────────────────────

function RoomsSide({ rooms, allocationsByRoom, equipment, onDropOnRoom, onEditAllocation, onRemoveAllocation }) {
  return (
    <section className="flex-1 border-r border-line-strong flex flex-col min-w-0 bg-bg-base" data-testid="equipment-rooms-side">
      <div className="px-3 py-2 border-b border-line-strong sticky top-0 bg-bg-elevated z-10 flex items-center gap-2">
        <Building2 className="w-3 h-3 text-amber" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">ROOMS</span>
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute">· drop to allocate</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rooms.length === 0 && (
          <div className="p-12 text-center mono text-2xs uppercase tracking-widest text-ink-mute italic">
            No rooms — add one in the Rooms panel first.
          </div>
        )}
        {rooms.map((room) => (
          <RoomGroup
            key={room.id}
            room={room}
            items={allocationsByRoom.get(room.id) || []}
            equipment={equipment}
            onDropOnRoom={onDropOnRoom}
            onEditAllocation={onEditAllocation}
            onRemoveAllocation={onRemoveAllocation}
          />
        ))}
      </div>
    </section>
  );
}

function RoomGroup({ room, items, onDropOnRoom, onEditAllocation, onRemoveAllocation }) {
  const [open, setOpen] = useState(true);
  const [hover, setHover] = useState(false);
  const colorBar = roomColor(room.color_token);
  const totalQty = items.reduce((s, x) => s + x.qty, 0);

  const onDragOver = (e) => {
    const types = Array.from(e.dataTransfer.types);
    if (!types.includes(MIME_INVENTORY) && !types.includes(MIME_ALLOCATION)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setHover(true);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    const raw = e.dataTransfer.getData(MIME_INVENTORY) || e.dataTransfer.getData(MIME_ALLOCATION);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload.equipment_id) onDropOnRoom(room, payload);
    } catch (err) { console.warn(err); }
  };

  return (
    <section
      className={
        "border-b border-line transition-colors " +
        (hover ? "outline outline-2 outline-amber outline-offset-[-2px] bg-amber-glow/10" : "")
      }
      onDragOver={onDragOver}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      data-testid={`eq-room-${room.id}`}
    >
      <header
        className="flex items-center gap-2 px-3 py-2 bg-bg-elevated cursor-pointer hover:bg-amber-glow/10 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="shrink-0">
          {open ? <ChevronDown className="w-3 h-3 text-ink-mute" /> : <ChevronRight className="w-3 h-3 text-ink-mute" />}
        </span>
        <span className="w-1.5 h-5 shrink-0" style={{ background: colorBar }} />
        <span className="mono text-2xs uppercase tracking-widest text-ink truncate">
          {room.abbreviation} · {room.name}
        </span>
        <span className="mono text-2xs text-ink-mute ml-auto tabular-nums">
          {items.length} ITEM{items.length === 1 ? "" : "S"} · {totalQty} UNIT{totalQty === 1 ? "" : "S"}
        </span>
      </header>

      {open && (
        <>
          {items.length === 0 ? (
            <div className="px-3 py-3 mono text-2xs uppercase tracking-widest text-ink-mute/60 italic">
              {hover ? "drop here = allocate" : "— drop equipment here —"}
            </div>
          ) : (
            <ul className="p-2 grid grid-cols-2 lg:grid-cols-3 gap-1.5">
              {items.map(({ equipment, allocation, qty }) => (
                <AllocationCard
                  key={allocation.id}
                  equipment={equipment}
                  allocation={allocation}
                  qty={qty}
                  onEdit={() => onEditAllocation(allocation)}
                  onRemove={() => onRemoveAllocation(allocation)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// ── INVENTORY side ──────────────────────────────────────────────

function InventorySide({ project, rooms, inventory, usageByEquipment, onPatchEquipment, onRemoveEquipment, onRefresh, filter, categories, search, onSearchChange, onOpenEquipment }) {
  return (
    <aside
      className="w-[480px] shrink-0 bg-bg-elevated flex flex-col min-h-0"
      data-testid="equipment-inventory-side"
    >
      <div className="px-3 py-2 border-b border-line-strong sticky top-0 bg-bg-elevated z-10 flex items-center gap-2">
        <Box className="w-3 h-3 text-amber" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">INVENTORY</span>
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute">· drag to allocate · click to edit</span>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-line bg-bg-base">
        <div className="relative">
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search label, category, vendor, notes…"
            className="w-full bg-bg-elevated border border-line-strong focus:border-amber outline-none text-ink text-xs px-2 py-1.5 pr-7"
            data-testid="inventory-search-input"
            autoComplete="off"
            spellCheck={false}
          />
          {search && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink"
              data-testid="inventory-search-clear"
              title="Clear search"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-3 py-2 border-b border-line">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1.5">+ NEW EQUIPMENT</div>
          <QuickAddForm project={project} rooms={rooms} categories={categories} onCreated={onRefresh} defaultKind={filter === "hired" ? "hired" : "purchased"} />
        </div>

        {inventory.length === 0 ? (
          <div className="px-3 py-6 mono text-2xs uppercase tracking-widest text-ink-mute italic">
            {search ? `— no equipment matches "${search}" —` : "— no equipment matches this filter —"}
          </div>
        ) : (
          <ul className="p-2 grid grid-cols-2 gap-1.5">
            {inventory.map((eq) => {
              const used = usageByEquipment.get(eq.id) || 0;
              const available = (eq.quantity_total || 0) - used;
              return (
                <InventoryCard
                  key={eq.id}
                  equipment={eq}
                  used={used}
                  available={available}
                  onOpen={() => onOpenEquipment(eq)}
                />
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

function QuickAddForm({ project, categories, onCreated, defaultKind }) {
  const [label, setLabel] = useState("");
  const [qty, setQty] = useState("1");
  const [kind, setKind] = useState(defaultKind || "purchased");
  const [cost, setCost] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (defaultKind && defaultKind !== "all") setKind(defaultKind); }, [defaultKind]);

  const submit = async () => {
    if (!project || !label.trim()) return;
    setBusy(true);
    try {
      await equipmentApi.create(project.id, {
        type: label.trim().toLowerCase().split(" ")[0].replace(/[^a-z0-9_]/g, "_"),
        label: label.trim(),
        quantity_total: Math.max(0, parseInt(qty, 10) || 0),
        category: category.trim() || null,
        acquisition_kind: kind,
        cost_amount: cost === "" ? null : parseFloat(cost),
        cost_currency: "USD",
      });
      setLabel(""); setQty("1"); setCost("");
      onCreated && (await onCreated());
      toast.success("Equipment added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } };

  return (
    <div className="space-y-1.5">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
        placeholder="Label · e.g. DMX Splitter — 4-way"
        className="w-full bg-bg-base border border-line-strong text-ink text-xs px-2 py-1"
        data-testid="eq-add-label"
        autoComplete="off" spellCheck={false}
      />
      <div className="grid grid-cols-[1fr_auto_auto] gap-1.5">
        <div className="flex border border-line-strong">
          {["hired", "purchased"].map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              data-testid={`eq-add-kind-${k}`}
              className={
                "mono text-2xs uppercase tracking-widest px-2 py-1 flex-1 transition-colors " +
                (kind === k
                  ? (k === "hired" ? "bg-amber text-bg-base" : "bg-emerald-400 text-bg-base")
                  : "text-ink-mute hover:text-ink")
              }
            >
              {k === "hired" ? "HIRED" : "PURCHASED"}
            </button>
          ))}
        </div>
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="pool"
          title="Total in inventory (allocations may exceed this → shortfall)"
          className="bg-bg-base border border-line-strong text-ink mono text-xs px-2 py-1 w-14 tabular-nums"
          data-testid="eq-add-qty"
        />
        <div className="flex items-center bg-bg-base border border-line-strong w-24">
          <DollarSign className="w-3 h-3 text-ink-mute ml-1.5 shrink-0" />
          <input
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="cost"
            title={kind === "hired" ? "Total hire cost" : "Purchase price"}
            className="bg-transparent text-ink mono text-xs px-1.5 py-1 w-full tabular-nums"
            data-testid="eq-add-cost"
          />
        </div>
      </div>
      <div className="flex gap-1.5">
        <div className="flex-1 min-w-0">
          <CategorySelect
            value={category}
            categories={categories}
            onChange={setCategory}
            placeholder="— UNCATEGORIZED —"
            buttonClassName="w-full"
            menuWidth={260}
            testid="eq-add-category"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || !label.trim()}
          className="btn-amber px-3 py-1 mono text-2xs uppercase tracking-widest disabled:opacity-40"
          data-testid="eq-add-submit"
        >
          <Plus className="w-3 h-3 inline mr-1" /> ADD
        </button>
      </div>
    </div>
  );
}

// ── ALLOCATION card (rooms side) ────────────────────────────────

function AllocationCard({ equipment, allocation, qty, onEdit, onRemove }) {
  const kindStyle = KIND_STYLES[equipment.acquisition_kind] || { dot: "bg-ink-mute", text: "text-ink-mute", label: "—" };
  const onDragStart = (e) => {
    const payload = JSON.stringify({
      kind: MIME_ALLOCATION,
      equipment_id: equipment.id,
      allocation_id: allocation.id,
      current_qty: qty,
    });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(MIME_ALLOCATION, payload);
  };
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onClick={onEdit}
      className="border bg-bg-base border-line hover:border-amber p-2 cursor-grab active:cursor-grabbing group transition-colors flex flex-col gap-1"
      data-testid={`eq-alloc-${allocation.id}`}
      title="Click to edit qty · drag to another room to move"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="w-3 h-3 text-ink-mute shrink-0 mt-0.5 opacity-50 group-hover:opacity-100" />
        <span className="text-sm text-ink truncate flex-1 select-none" title={equipment.label || equipment.type}>
          {equipment.label || equipment.type}
        </span>
        <span className="mono text-base text-amber tabular-nums shrink-0">×{qty}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="opacity-0 group-hover:opacity-100 text-red hover:text-red-light p-0.5 shrink-0 transition-opacity"
          data-testid={`eq-alloc-remove-${allocation.id}`}
          title="Remove from this room"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-2 text-2xs mono uppercase tracking-widest">
        <span className={`flex items-center gap-1 ${kindStyle.text}`}>
          <span className={`w-1.5 h-1.5 ${kindStyle.dot}`} />
          {kindStyle.label}
        </span>
        {equipment.category && (
          <>
            <span className="opacity-40">·</span>
            <span className="text-ink-mute">{equipment.category}</span>
          </>
        )}
      </div>
    </li>
  );
}

// ── INVENTORY card (inventory side) — drag to room or click to edit ─────

function InventoryCard({ equipment, used, available, onOpen }) {
  const kindStyle = KIND_STYLES[equipment.acquisition_kind] || { dot: "bg-ink-mute", text: "text-ink-mute", label: "—" };
  const shortfall = available < 0;

  const onDragStart = (e) => {
    const payload = JSON.stringify({
      kind: MIME_INVENTORY,
      equipment_id: equipment.id,
      current_qty: 0,
    });
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(MIME_INVENTORY, payload);
  };

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onClick={onOpen}
      className={
        "border p-2 cursor-pointer group transition-colors flex flex-col gap-1 " +
        (shortfall ? "border-red bg-red-glow/10 hover:border-red-light" : "bg-bg-base border-line hover:border-amber")
      }
      data-testid={`eq-inv-${equipment.id}`}
      title="Click to edit · drag onto a room to allocate"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="w-3 h-3 text-ink-mute shrink-0 mt-0.5 opacity-50 group-hover:opacity-100" />
        <span className="text-sm text-ink truncate flex-1 select-none" data-testid={`eq-label-${equipment.id}`}>
          {equipment.label || equipment.type}
        </span>
      </div>

      {/* Availability strip. Red when negative (shortfall). */}
      <div className="flex items-center gap-2 text-2xs mono uppercase tracking-widest">
        <span className={shortfall ? "text-red font-semibold" : "text-cyan"} data-testid={`eq-avail-${equipment.id}`}>
          {shortfall ? `${available} SHORT` : `${available} AVAIL`}
        </span>
        <span className="opacity-40">/</span>
        <span className="text-ink-mute tabular-nums" data-testid={`eq-qty-${equipment.id}`}>
          {equipment.quantity_total} POOL
        </span>
        <span className="opacity-40">·</span>
        <span className="text-ink-mute tabular-nums" title="Allocated across all rooms">
          {used} USED
        </span>
      </div>

      <div className="flex items-center gap-2 text-2xs mono uppercase tracking-widest">
        <span className={`flex items-center gap-1 ${kindStyle.text}`} data-testid={`eq-kind-${equipment.id}`}>
          <span className={`w-1.5 h-1.5 ${kindStyle.dot}`} />
          {kindStyle.label}
        </span>
        {equipment.category && (
          <>
            <span className="opacity-40">·</span>
            <span className="text-amber" data-testid={`eq-cat-${equipment.id}`}>{equipment.category}</span>
          </>
        )}
        {equipment.cost_amount != null && (
          <>
            <span className="opacity-40">·</span>
            <span className="text-ink-mute tabular-nums" data-testid={`eq-cost-${equipment.id}`}>
              {equipment.cost_currency || "USD"} {formatCost(equipment.cost_amount)}
            </span>
          </>
        )}
      </div>
    </li>
  );
}

function formatCost(n) {
  if (typeof n !== "number") return "";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
